import {DataTable} from '@/components-v2'
import {Text} from '@/components-v2/atoms'
import {Modal} from '@/components-v2/organisms'
import {usePromotionReview} from '@/hooks/usePromotionReview'
import {PromotionType} from '@/types/promotion'
import {useState} from 'react'
import './PromotionPreview.scss'

/**
 * PromotionPreview
 * Renders a preview of the promotion based on the provided conditions and active tab.
 * @param {object} params
 * @param {Array} params.conditions - Promotion conditions array
 * @param {number} params.activePromoCondition - Active tab index
 * @param {string} params.countryCode - Country code for currency
 * @param {string} params.promotionType - Type of the promotion
 * @returns {JSX.Element} The promotion preview component
 */

export const PromotionPreview = ({conditions, activePromoCondition, countryCode, promotionType}) => {
  const {createPreviewMessage, isMoreBtnVisible} = usePromotionReview({
    conditions,
    activePromoCondition,
    countryCode,
    promotionType,
  })

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

  const supportedTypes = [
    PromotionType.BUY_SINGLE_PRODUCT,
    PromotionType.BUY_MULTIPLE_PRODUCTS,
    PromotionType.STORE_DISCOUNT,
    PromotionType.WELCOME_DISCOUNT,
  ]

  const productRowX = conditions[0]?.conditionGroup?.conditions[0]?.items || []
  const productRowsY = conditions[0]?.conditionGroup?.conditions[1]?.items || []
  const productColumns = [{columnKey: 'product', header: ''}]
  const products_x = productRowX.map(product => ({
    id: product?.id,
    product: product?.name || product?.id,
  }))
  const products_y = productRowsY.map(product => ({
    id: product?.id,
    product: product?.name || product?.id,
  }))
  const reward = conditions[0]?.rewards?.[0]
  const isFreeProduct =
    reward?.discountType === 'FREE_PRODUCT' ||
    reward?.type === 'FREE_PRODUCT' ||
    reward?.type === 'FREE_PRODUCT_FROM_LIST'
  const freeProducts =
    reward?.type === 'FREE_PRODUCT_FROM_LIST'
      ? [...(reward.mandatoryFreeProducts ?? []), ...(reward.freeProductLists ?? []).flatMap(list => list.items ?? [])]
      : isFreeProduct
        ? reward?.items || []
        : []
  const freeProductList = freeProducts.map(product => ({
    id: product?.id,
    product: product?.name || product?.id,
  }))

  const previewContent = (
    <div className="flex h-full flex-col gap-5">
      {createPreviewMessage(true, true)}
      <div className="flex flex-1 flex-row items-stretch gap-10">
        <div className="promotion-preview-table flex flex-1 flex-col gap-3">
          <Text variant="bold">Product List X:</Text>
          <DataTable
            columns={productColumns}
            data={products_x}
            enableSorting={false}
            selection="none"
            showPagination={false}
            showSearchOption={false}
            showTableHeader={false}
            maxHeight="300px"
            noDataTitleText="No products selected"
          />
        </div>
        {promotionType === PromotionType.BUY_MULTIPLE_PRODUCTS && (
          <div className="promotion-preview-table flex flex-1 flex-col gap-3">
            <Text variant="bold">Product List Y:</Text>
            <DataTable
              columns={productColumns}
              data={products_y}
              enableSorting={false}
              selection="none"
              showPagination={false}
              showSearchOption={false}
              showTableHeader={false}
              maxHeight="300px"
              noDataTitleText="No products selected"
            />
          </div>
        )}
      </div>
      {isFreeProduct && (
        <div className="promotion-preview-table flex flex-1 flex-col gap-3">
          <Text variant="bold">Free Products:</Text>
          <DataTable
            columns={productColumns}
            data={freeProductList}
            enableSorting={false}
            selection="none"
            showPagination={false}
            showSearchOption={false}
            showTableHeader={false}
            maxHeight="300px"
            noDataTitleText="No products selected"
          />
        </div>
      )}
    </div>
  )

  return (
    <div>
      {supportedTypes.includes(promotionType) && (
        <div className="flex flex-col gap-1">
          <Text variant="bold">Preview:</Text>
          {createPreviewMessage(false)}
          {isMoreBtnVisible && (
            <Text
              variant="bold"
              textColorClass="more-btn-color"
              style={{cursor: 'pointer'}}
              onClick={() => console.log('opening preview modal') || setIsPreviewModalOpen(true)}
            >
              More...
            </Text>
          )}
        </div>
      )}

      <Modal
        showModal={isPreviewModalOpen}
        title="Preview"
        type="M"
        showSlot={true}
        hideCloseButton={true}
        showTertiaryButton={false}
        primaryButtonProps={{text: 'Close', onClick: () => setIsPreviewModalOpen(false)}}
        onCloseModal={() => setIsPreviewModalOpen(false)}
      >
        {previewContent}
      </Modal>
    </div>
  )
}
